import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";
import lesson0418 from "../lesson-18/lesson";

const linkLesson0418 = getLinkForCourseLesson("e0db4477-1995-4380-9c35-2338dcf5e504", lesson0418.id);

const theory = `<h3 class=sectionHead>Introduction</h3>
<p>Manufacturers of moving coil loudspeakers usually provide data sheets with relevant information about their products, <em><span class=cmti-10>i.e.</span></em> photos, drawings, data, etc. Among the specifications, the so-called Thiele & Small (T&S) parameters [2] are provided. The objectives of this Lesson is to introduce these parameters, to relate them to the model developed in <a href="${linkLesson0418}" target="_blank">Lesson 18</a> and to present a simple measurement method to evaluate them.<br class=newline>
<h3 class=sectionHead>T&S parameters</h3>
<p>In the previous Lesson, a model of the moving coil loudspeaker has been derived allowing the calculation of the drivers electrical impedance as well as the diaphragm velocity and on-axis pressure frequency responses. To perform the calculation, the following seven fundamental input parameters must be known:
<ul class=itemize1>
   <li class=itemize>
      mechanical parameters of the 1-DOF system equivalent to the moving parts of the loudspeaker: mass
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>M</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>ms</mi>
            </mrow>
         </msub>
      </math>
      , resistance
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>ms</mi>
            </mrow>
         </msub>
      </math>
      , and compliance
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>C</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>ms</mi>
            </mrow>
         </msub>
      </math>
      ,
   <li class=itemize>
      electrical parameters: resistance
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi>e</mi>
            </mrow>
         </msub>
      </math>
      and inductance
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>L</mi>
            </mrow>
            <mrow>
               <mi>e</mi>
            </mrow>
         </msub>
      </math>
      of the voice coil,
   <li class=itemize>
      coupling parameters: surface area of the diaphragm
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>S</mi>
            </mrow>
            <mrow>
               <mi>d</mi>
            </mrow>
         </msub>
      </math>
      which links the mechanical domain to the acoustical on and
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi mathvariant=italic>Bℓ</mi>
      </math>
      factor which links the mechanical domain to the electrical one.
</ul>
<p>The complete set of fundamental parameters determines the loudspeaker response according to the lumped-parameter model. In general, the fundamental parameters can be individually modified by replacing or manipulating the physical parts of the loudspeaker. For example, replace the magnet or change the magnetic circuit and <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi mathvariant=italic>Bℓ</mi>
      </math> will be modified; replace the spider by a softer one and the compliance will be increased; replace the membrane by another one made of a lighter material and the moving mass will be decreased. Some of the parameters are dependant, for instance, increasing the length of the <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi mathvariant=italic>ℓ</mi>
      </math> of the voice coil also increases the mass       <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>M</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>ms</mi>
            </mrow>
         </msub>
      </math>.
<p>Nevertheless, it is actually difficult to infer the loudspeaker's behaviour directly from the values of the fundamental parameters. In other words, none of the fundamental parameters is meaningful just by itself. For example, which is the response of a driver with a compliance of 1300 μm/N? Is it an appropriate value? Should it be increased or decreased? No one can give an answer to this question just with this information. The same occurs with the rest of parameters. How about a driver with a moving mass of 110 g? Is 21 N/A a good value for <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi mathvariant=italic>Bℓ</mi>
      </math>?  
<p>To overcome these problems, the Australian electroacousticians A. Neville Thiele and Richard H. Small defined a set of linear parameters that provide a more meaningful interpretation about loudspeakers behaviour and the applications they are intended to. These parameters, nowadays known as  (T&S) parameters, are defined in the low-frequency range and using a small input voltage in order to keep the behaviour as linear as possible. That's the reason why they are also called small-signals parameters. As they are measured at low frequencies, the effect of the inductance can be neglected, reducing the number of parameters needed to model the loudspeaker behaviour to six. Small-signal parameters can be expressed as a function of the fundamental parameters defined above:
<ul class=itemize1>
   <li class=itemize>
      The resonance frequency
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>f</mi>
            </mrow>
            <mrow>
               <mi>s</mi>
            </mrow>
         </msub>
      </math>
      of the voice coil:
    <table class=equation>
         <tr>
            <td>
               <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
         <msub>
            <mrow>
               <mi>f</mi>
            </mrow>
            <mrow>
               <mi>s</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <mfrac>
            <mrow>
               <msub>
                  <mrow>
                     <mi>ω</mi>
                  </mrow>
                  <mrow>
                     <mi>s</mi>
                  </mrow>
               </msub>
            </mrow>
            <mrow>
               <mn>2</mn>
               <mi>π</mi>
            </mrow>
         </mfrac>
         <mo class=MathClass-rel>=</mo>
         <mfrac>
            <mrow>
               <mn>1</mn>
            </mrow>
            <mrow>
               <mn>2</mn>
               <mi>π</mi>
            </mrow>
         </mfrac>
         <mfrac>
            <mrow>
               <mn>1</mn>
            </mrow>
            <mrow>
               <msqrt>
                  <mrow>
                     <msub>
                        <mrow>
                           <mi>M</mi>
                        </mrow>
                        <mrow>
                           <mi mathvariant=italic>ms</mi>
                        </mrow>
                     </msub>
                     <msub>
                        <mrow>
                           <mi>C</mi>
                        </mrow>
                        <mrow>
                           <mi mathvariant=italic>ms</mi>
                        </mrow>
                     </msub>
                  </mrow>
               </msqrt>
            </mrow>
         </mfrac>
      </math>
                <td class=eq-no>(1)
      </table>
     This parameter reflects how much the frequency response can be extended towards low frequencies. The lower the <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>f</mi>
            </mrow>
            <mrow>
               <mi>s</mi>
            </mrow>
       </msub></math>, the better the low-frequency response. There are two ways to decrease <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>f</mi>
            </mrow>
            <mrow>
               <mi>s</mi>
            </mrow>
       </msub></math>, either increasing the moving mass (by means of a heavier diaphragm) or increasing the compliance (using a softer spider).
       <li class=itemize>The quality factor <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ts</mi>
         </mrow>
      </msub>
   </math> reflects the behaviour of the frequency response around <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>f</mi>
            </mrow>
            <mrow>
               <mi>s</mi>
            </mrow>
       </msub></math>. A <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ts</mi>
         </mrow>
      </msub>
   </math> of 0.707 presents a flat <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msup>
            <mrow>
               <mn>2</mn>
            </mrow>
            <mrow>
               <mn>nd</mn>
            </mrow>
       </msup></math>-order Butterworth response. Higher <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ts</mi>
         </mrow>
      </msub>
   </math> values presents a peak around <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>f</mi>
            </mrow>
            <mrow>
               <mi>s</mi>
            </mrow>
       </msub></math> which is reflected as a low-frequency boost. On the other hand, lower values result in a smoother transition between the roll-off and flat regions (see Figures <a href="#" data-img-src=${config.ASSETS_URL}/04/19/theory/figure-1.svg data-img-alt="Figure 1. Normalised diaphragm displacement for different <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>Q</mi></mrow><mrow><mi mathvariant=italic>ts</mi></mrow></msub></math> values.">1</a> and <a href="#" data-img-src=${config.ASSETS_URL}/04/19/theory/figure-2.svg data-img-alt="Figure 2. On-axis normalised pressure level for different <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>Q</mi></mrow><mrow><mi mathvariant=italic>ts</mi></mrow></msub></math> values.">2</a>). The total quality factor 
           <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ts</mi>
         </mrow>
      </msub>
   </math> actually depends on the electrical and mechanical quality factors (<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>es</mi>
         </mrow>
      </msub>
   </math> and <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
   </math>, respectively) which, in turn, are defined from the fundamental parameters:
<table class=equation>
         <tr>
            <td>
               <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
                  <msub>
                     <mrow>
                        <mi>Q</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>es</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-rel>=</mo>
                  <mfrac>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>ω</mi>
                           </mrow>
                           <mrow>
                              <mi>s</mi>
                           </mrow>
                        </msub>
                        <msub>
                           <mrow>
                              <mi>M</mi>
                           </mrow>
                           <mrow>
                              <mi mathvariant=italic>ms</mi>
                           </mrow>
                        </msub>
                        <msub>
                           <mrow>
                              <mi>R</mi>
                           </mrow>
                           <mrow>
                              <mi>e</mi>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <mi>B</mi>
                        <msup>
                           <mrow>
                              <mi>ℓ</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msup>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-rel>=</mo>
                  <mfrac>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>R</mi>
                           </mrow>
                           <mrow>
                              <mi>e</mi>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <mi>B</mi>
                        <msup>
                           <mrow>
                              <mi>ℓ</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msup>
                     </mrow>
                  </mfrac>
                  <msqrt>
                     <mrow>
                        <mfrac>
                           <mrow>
                              <msub>
                                 <mrow>
                                    <mi>M</mi>
                                 </mrow>
                                 <mrow>
                                    <mi mathvariant=italic>ms</mi>
                                 </mrow>
                              </msub>
                           </mrow>
                           <mrow>
                              <msub>
                                 <mrow>
                                    <mi>C</mi>
                                 </mrow>
                                 <mrow>
                                    <mi mathvariant=italic>ms</mi>
                                 </mrow>
                              </msub>
                           </mrow>
                        </mfrac>
                     </mrow>
                  </msqrt>
               </math>
            <td class=eq-no>(2)
      </table>
<table class=equation>
         <tr>
            <td>
               <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
                  <msub>
                     <mrow>
                        <mi>Q</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ms</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-rel>=</mo>
                  <mfrac>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>ω</mi>
                           </mrow>
                           <mrow>
                              <mi>s</mi>
                           </mrow>
                        </msub>
                        <msub>
                           <mrow>
                              <mi>M</mi>
                           </mrow>
                           <mrow>
                              <mi mathvariant=italic>ms</mi>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>R</mi>
                           </mrow>
                           <mrow>
                              <mi mathvariant=italic>ms</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-rel>=</mo>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>R</mi>
                           </mrow>
                           <mrow>
                              <mi mathvariant=italic>ms</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
                  <msqrt>
                     <mrow>
                        <mfrac>
                           <mrow>
                              <msub>
                                 <mrow>
                                    <mi>M</mi>
                                 </mrow>
                                 <mrow>
                                    <mi mathvariant=italic>ms</mi>
                                 </mrow>
                              </msub>
                           </mrow>
                           <mrow>
                              <msub>
                                 <mrow>
                                    <mi>C</mi>
                                 </mrow>
                                 <mrow>
                                    <mi mathvariant=italic>ms</mi>
                                 </mrow>
                              </msub>
                           </mrow>
                        </mfrac>
                     </mrow>
                  </msqrt>
               </math>
            <td class=eq-no>(3)
      </table>
   with 
  <table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>Q</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ts</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Q</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ms</mi>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>Q</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>es</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Q</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ms</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>Q</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>es</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
          </math>
      <td class=eq-no>(4)
</table>  
In most drivers ---except for mini- and micro-speakers--- <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>es</mi>
         </mrow>
      </msub>
   </math> is much lower than <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
   </math> and therefore, <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ts</mi>
         </mrow>
      </msub>
   </math> is dominated by <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>es</mi>
         </mrow>
      </msub>
   </math>. Under these circumstances, <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ts</mi>
         </mrow>
      </msub>
   </math> is slightly lower than <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>es</mi>
         </mrow>
      </msub>
   </math>, and the way to modify it is by changing any of the fundamental parameters involved in Eq. (2). It should be noted that the total quality factor of the system will be increased when mounting the driver on an enclosure. Taking this into account, woofers are generally designed with <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ts</mi>
         </mrow>
      </msub>
   </math> between 0.2 and 0.6.
  <li class=itemize>The efficiency <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>η</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math> represents the ratio between the radiated acoustical power <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>P</mi>
         </mrow>
         <mrow>
            <mn>ar</mn>
         </mrow>
      </msub>
   </math> and the electrical input power <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>P</mi>
         </mrow>
         <mrow>
            <mn>e</mn>
         </mrow>
      </msub>
   </math>
      <table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>η</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>0</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>P</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ar</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>P</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>e</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
          </math>
      <td class=eq-no>(5)
</table>
 The efficiency can be also expressed from the fundamental parameters           
 <table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>η</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>0</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
             <mfrac>           
                <mrow>
                   <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                </mrow>
                <mrow>
                    <mn>2</mn>
                    <mi>πc</mi>
                </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                    <mi>B</mi>
                    <msup>
                           <mrow>
                              <mi>ℓ</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                    </msup>
                    <msubsup>
                        <mrow>
                            <mi>S</mi>
                            </mrow>
                            <mrow>
                                <mi>d</mi>
                            </mrow>
                        <mrow>
                    <mn>2</mn>
                    </mrow>
                    </msubsup>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>e</mi>
                     </mrow>
                  </msub>
                   <msub>
                     <mrow>
                        <mi>M</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ms</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
          </math>
      <td class=eq-no>(6)
</table>
The higher the efficiency, the higher the sound pressure generated for the same input power and,
     therefore, the better. To increase the efficiency, any of the following modifications could be done:
     increase <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi mathvariant=italic>Bℓ</mi>
      </math> (for example by using a more powerful magnet); increase 
     <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>S</mi>
            </mrow>
            <mrow>
               <mi>d</mi>
            </mrow>
         </msub>
      </math> by using a larger diaphragm; decreasing 
     <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>M</mi>
            </mrow>
            <mrow>
               <mi>ms</mi>
            </mrow>
         </msub>
      </math> (for example, by using a diaphragm with a lighter material such as carbonfiber or titanium) or decreasing
     <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi>e</mi>
            </mrow>
         </msub>
      </math> by using a voice coil with a different wiring material.
 <li class=itemize>
      The equivalent volume
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>V</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>as</mi>
            </mrow>
         </msub>
      </math>
      represents the volume of air required to achieve a compliance equivalent to that of the moving part, and is given by
  <table class=equation>
         <tr>
            <td>
               <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
                  <msub>
            <mrow>
               <mi>V</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>as</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <msub>
            <mrow>
               <mi>ρ</mi>
            </mrow>
            <mrow>
               <mn>0</mn>
            </mrow>
         </msub>
         <msup>
            <mrow>
               <mi>c</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msup>
         <msubsup>
            <mrow>
               <mi>S</mi>
            </mrow>
            <mrow>
               <mi>d</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msubsup>
         <msub>
            <mrow>
               <mi>C</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>ms</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <msub>
            <mrow>
               <mi>ρ</mi>
            </mrow>
            <mrow>
               <mn>0</mn>
            </mrow>
         </msub>
         <msup>
            <mrow>
               <mi>c</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msup>
         <msub>
            <mrow>
               <mi>C</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>as</mi>
            </mrow>
         </msub>
               </math>
            <td class=eq-no>(7)
      </table>   
This parameter will exert a strong influence on the cabinet size required to achieve a good design. The larger the 
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>V</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>as</mi>
            </mrow>
         </msub>
      </math>, the larger the volume required. As compact cabinets are preferred to large ones, the lower the 
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>V</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>as</mi>
            </mrow>
         </msub>
      </math> the better. The equivalent volume <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>V</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>as</mi>
            </mrow>
         </msub>
      </math> can be decreased either by decreasing the compliance or the size of the membrane.      
</ul>
<p><img src=${config.ASSETS_URL}/04/19/theory/figure-1.svg alt="Figure 1. Normalised diaphragm displacement for different <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>Q</mi></mrow><mrow><mi mathvariant=italic>ts</mi></mrow></msub></math> values."><figcaption class=caption>Figure 1. Normalised diaphragm displacement for different <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>Q</mi></mrow><mrow><mi mathvariant=italic>ts</mi></mrow></msub></math> values.</figcaption></figure>
<p><img src=${config.ASSETS_URL}/04/19/theory/figure-2.svg alt="Figure 2. On-axis normalised pressure level for different <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>Q</mi></mrow><mrow><mi mathvariant=italic>ts</mi></mrow></msub></math> values."><figcaption class=caption>Figure 2. On-axis normalised pressure level for different <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>Q</mi></mrow><mrow><mi mathvariant=italic>ts</mi></mrow></msub></math> values.</figcaption></figure>
    It is thus possible to relate all the small-signal parameters to the fundamental parameters. Actually, the complete set of T&S parameters involve both fundamental and small-signal parameters. 

<h3 class=sectionHead>Conflict between small-signal parameters</h3>
It should be noted that small-signal parameters can not be individually modified. Increase <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mn>ms</mn>
         </mrow>
      </msub>
   </math> to improve (decrease) <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mn>s</mn>
         </mrow>
      </msub>
   </math> and as a result, <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>η</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math> will be worsened (decreased). As a side effect, <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mn>es</mn>
         </mrow>
      </msub>
   </math> (and in turn, <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mn>ts</mn>
         </mrow>
      </msub>
   </math>) will be increased. Increase now <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mn>d</mn>
         </mrow>
      </msub>
   </math> to compensate the decrease of <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>η</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math> and, as a result, <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mn>as</mn>
         </mrow>
      </msub>
   </math> will be worsened (increased). You could then decrease <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mn>ms</mn>
         </mrow>
      </msub>
   </math> to compensate for the latter effect on <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mn>as</mn>
         </mrow>
      </msub>
   </math>, but <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mn>s</mn>
         </mrow>
      </msub>
   </math> will be increased as a consequence (and also, the quality factor will be increased). Change now <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi mathvariant=italic>Bℓ</mi>
      </math> to compensate for the increase in the quality factor, and the efficiency will be decreased. Come full circle. This occurs because there is a physical constraint that hinders from improving a single small-signal parameter without worsening at least some of the others. This constraint is reflected in the following equation
    </table>
 The efficiency can be also expressed from the fundamental parameters           
 <table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>η</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>0</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
             <mfrac>           
                <mrow>
                    <mn>4</mn>
                   <msup>
                     <mrow>
                        <mi>π</mi>                    
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                </mrow>
                <mrow>
                    <msup>
                           <mrow>
                              <mi>c</mi>
                           </mrow>
                           <mrow>
                              <mn>3</mn>
                           </mrow>
                    </msup>
                </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                    <msubsup>
                        <mrow>
                            <mi>f</mi>
                            </mrow>
                            <mrow>
                                <mi>s</mi>
                            </mrow>
                        <mrow>
                    <mn>3</mn>
                    </mrow>
                    </msubsup>
                   <msub>
                     <mrow>
                        <mi>V</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>as</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Q</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>es</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
          </math>
      <td class=eq-no>(8)
</table>
	As can be appreciated, it is not possible to improve the efficiency without affecting the bass-response or the equivalent volume. The balance between <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>η</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>, <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mn>s</mn>
         </mrow>
      </msub>
   </math>, <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mn>as</mn>
         </mrow>
      </msub>
   </math> and quality factors will define the type of loudspeaker and its applications. Let us prioritize low <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mn>s</mn>
         </mrow>
      </msub>
   </math> and high efficiency in loudspeakers intended for concerts in large arenas, at the expense of large <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mn>as</mn>
         </mrow>
      </msub>
   </math>. On the other hand, prioritize low <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mn>as</mn>
         </mrow>
      </msub>
   </math> for portable devices at the expense of worsening the frequency response and/or the efficiency. Apart from those examples, there is a large number of applications including HiFi, monitors, car audio, paging or smart devices, among other. 
     
     <div class=table>
   <figure class=float>
      <figcaption class=caption>Table 1. Examples of T&S parameters for different drivers.</figcaption>
      <img alt="Table 1. Examples of T&S parameters for different drivers." src=${config.ASSETS_URL}/04/19/theory/table-1.svg>
   </figure>
</div>
     
<p><a href=# data-img-src=${config.ASSETS_URL}/04/19/theory/table-1.svg data-img-alt="Table 1. Examples of T&S parameters for different drivers.">Table 1</a> describes small-signal parameters of several drivers intended for different applications. As can be observed, the Beyma 21" is designed to achieve a profound bass response and high efficiency (although it could sound strange, 3% can be considered a high efficiency for a dynamic loudspeaker). As a trade-off, this driver has a large <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mn>as</mn>
         </mrow>
      </msub>
   </math>. This driver can be appropriate for subwoofers to be used in large arenas where the volume of the cabinet is not an issue. The B&C 12" is designed to achieve lower <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mn>as</mn>
         </mrow>
      </msub>
   </math> at the expense of increasing <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mn>s</mn>
         </mrow>
      </msub>
   </math> and decreasing efficiency, and can be appropriate in PA applications where a more compact cabinet is required. Moreover, the SEAS 5", with a <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mn>as</mn>
         </mrow>
      </msub>
   </math> of just 5 l, could be appropriate for a HiFi system, where the efficiency is not that prioritary. Finally, the Dayton 1-1/4" is a mini-speaker with very low <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mn>as</mn>
         </mrow>
      </msub>
   </math>, at the expense of loosing low frequencies and reaching a very low efficiency. This driver is appropriate for sound reproduction in portable devices at a close distance to the listener. Beyond these examples, manufacturers offer a broad catalogue of drivers with different balance of T&S parameter depending on the target application.
<h3 class=sectionHead>Measurement of the T&S parameters</h3>
<p>Knowledge of the T&S parameters is of particular interest when it comes to dimensioning the size and type of acoustic enclosure. This is especially true since the parameters provided by the manufacturers may have changed and are therefore partly obsolete, e.g. change of suspension model due to unavailability. The possibility of measuring them with relatively common equipment is therefore very attractive.<br class=newline>Thanks to the electro-mechanical-acoustic coupling, the T&S parameters can be obtained can be obtained by measuring the electrical impedance of the loudspeaker [1], and in particular by using the hump observed at low frequency which is due to the resonance of the moving parts. In summary, electrical impedance measurements provide the following benefits:
<ul class=itemize1>
   <li class=itemize>good sensitivity to the mechanical parameters of the loudspeaker at low frequencies,
   <li class=itemize>relative safety of the measurement to the acoustic environment (even if the loudspeaker also works in microphone mode, its sensitivity is sufficiently low),
   <li class=itemize>high accuracy of electrical voltage measurements.
</ul>
<p>
   The low frequency part of the electrical impedance of the driver
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>et</mi>
         </mrow>
      </msub>
   </math>
   is given in <a href="#" data-img-src=${config.ASSETS_URL}/04/19/theory/figure-3.svg data-img-alt="Figure 3. T&S parameters measurement principle.">Figure 3</a>. Let us define the frequency
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>, with
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>></mo>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   such as
</p>
<math display=block xmlns=http://www.w3.org/1998/Math/MathML>
   <msub>
      <mrow>
         <mi>Z</mi>
      </mrow>
      <mrow>
         <mi mathvariant=italic>et</mi>
      </mrow>
   </msub>
   <mo class=MathClass-open>(</mo>
   <msub>
      <mrow>
         <mi>f</mi>
      </mrow>
      <mrow>
         <mn>1</mn>
      </mrow>
   </msub>
   <mo class=MathClass-punc>,</mo>
   <msub>
      <mrow>
         <mi>f</mi>
      </mrow>
      <mrow>
         <mn>2</mn>
      </mrow>
   </msub>
   <mo class=MathClass-close>)</mo>
   <mo class=MathClass-rel>=</mo>
   <msqrt>
      <mrow>
         <mi>ζ</mi>
      </mrow>
   </msqrt>
   <msub>
      <mrow>
         <mi>R</mi>
      </mrow>
      <mrow>
         <mi>e</mi>
      </mrow>
   </msub>
   <mo class=MathClass-rel>=</mo>
   <msqrt>
      <mrow>
         <msub>
            <mrow>
               <mi>Z</mi>
            </mrow>
            <mrow>
               <mi>e</mi>
            </mrow>
         </msub>
         <mo class=MathClass-open>(</mo>
         <msub>
            <mrow>
               <mi>f</mi>
            </mrow>
            <mrow>
               <mi>s</mi>
            </mrow>
         </msub>
         <mo class=MathClass-close>)</mo>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi>e</mi>
            </mrow>
         </msub>
      </mrow>
   </msqrt>
</math>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>ζ</mi>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-bin>∕</mo>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
      <mo class=MathClass-bin>+</mo>
      <mfrac>
         <mrow>
            <mi>B</mi>
            <msup>
               <mrow>
                  <mi>ℓ</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>e</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ms</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
      <mo class=MathClass-bin>+</mo>
      <mfrac>
         <mrow>
            <msub>
               <mrow>
                  <mi>Q</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ms</mi>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>Q</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>es</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>.
   <figure class=figure>
      <img src=${config.ASSETS_URL}/04/19/theory/figure-3.svg alt="Figure 3. T&S parameters measurement principle.">
      <figcaption class=caption>Figure 3. T&S parameters measurement principle.</figcaption>
   </figure>
<p>One can show that
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>Q</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ms</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msqrt>
               <mrow>
                  <mi>ζ</mi>
               </mrow>
            </msqrt>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>f</mi>
                     </mrow>
                     <mrow>
                        <mi>s</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>f</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>−</mo>
                  <msub>
                     <mrow>
                        <mi>f</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(9)
</table>
<p>and that
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>Q</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>es</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Q</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>mes</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mi>ζ</mi>
                  <mo class=MathClass-bin>−</mo>
                  <mn>1</mn>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(10)
</table>
<p>
   Now let’s move on to a description of a method for measuring T&S parameters. In order to carry on this measurement, you will need a laptop, a sound card with one output and two inputs, a calibrated resistor of value
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>p</mi>
         </mrow>
      </msub>
   </math>
   to measure the current, a multimeter and some cables. The value of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>p</mi>
         </mrow>
      </msub>
   </math>
   is generally chosen to be of the order of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
   </math>.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/19/theory/figure-4.svg alt="Figure 4. T&S parameters measurement set-up."><figcaption class=caption>Figure 4. T&S parameters measurement set-up.</figcaption></figure>
<ul class=itemize1>
   <li class=itemize>
      By using a multimeter, get the value of
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi>e</mi>
            </mrow>
         </msub>
      </math>
      using the DC resistance mode. You can also check the value of
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi>p</mi>
            </mrow>
         </msub>
      </math>.
   <li class=itemize>
      connect the loudspeaker according to the diagram shown in the <a href="#" data-img-src=${config.ASSETS_URL}/04/19/theory/figure-4.svg data-img-alt="Figure 4. T&S parameters measurement set-up.">Figure 4</a>. Play a signal with enough energy in the bandwidth of interest, which should include
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>f</mi>
            </mrow>
            <mrow>
               <mi>s</mi>
            </mrow>
         </msub>
      </math>. Record the input voltage of channels
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mn>1</mn>
      </math>
      &
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mn>2</mn>
      </math>
      respectively denoted
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>u</mi>
            </mrow>
            <mrow>
               <mn>1</mn>
            </mrow>
         </msub>
      </math>
      and
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>u</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msub>
      </math>,
   <li class=itemize>
      from the plot, get the values of
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>f</mi>
            </mrow>
            <mrow>
               <mi>s</mi>
            </mrow>
         </msub>
      </math>,
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi>η</mi>
      </math>,
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>f</mi>
            </mrow>
            <mrow>
               <mn>1</mn>
            </mrow>
         </msub>
      </math>
      and
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>f</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msub>
      </math>,
   <li class=itemize>
      using these values, calculate
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>Q</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>ms</mi>
            </mrow>
         </msub>
      </math>
      and
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>Q</mi>
            </mrow>
            <mrow>
               <mi mathvariant=italic>es</mi>
            </mrow>
         </msub>
      </math>
      from Eqs. (9) and (10).
</ul>
<p>
   So far, we have measured four T&S parameters, <em><span class=cmti-10>i.e.</span></em>
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
   </math>,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>es</mi>
         </mrow>
      </msub>
   </math>. The area
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
   </math>
   can be approximated by that of a disk whose radius is about halfway across the surround. At this extent, we have one more unknown parameters that the number of equations that relate all them and, therefore, can not estimate the rest of parameters unless we carry out new measurements.
   <h4 class=sectionHead>The added mass method</h4>
 One possible solution to continue is to provoke some controlled perturbation that modifies the resonance frequency. A simple and common procedure is by increasing the moving mass by stucking a known mass to the membrane ---also known as the added mass method. This mass must be of the order of 70$ of <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi>ms</mi>
         </mrow>
      </msub>
   </math> and be arranged in such a way as to limit the rotational movements of the diaphragm. As the total moving mass is now larger, the modified resonance frequency <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>sm</mi>
         </mrow>
      </msub>
   </math> becomes lower 
    <table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>f</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>sm</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi>π</mi>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <msqrt>
                     <mrow>
                        <mrow>
                           <mo form=prefix fence=true>(</mo>
                           <mrow>
                              <msub>
                                 <mrow>
                                    <mi>M</mi>
                                 </mrow>
                                 <mrow>
                                    <mi mathvariant=italic>ms</mi>
                                 </mrow>
                              </msub>
                              <mo class=MathClass-bin>+</mo>
                              <msub>
                                 <mrow>
                                    <mi>M</mi>
                                 </mrow>
                                 <mrow>
                                    <mi>a</mi>
                                 </mrow>
                              </msub>
                           </mrow>
                           <mo form=postfix fence=true>)</mo>
                        </mrow>
                        <msub>
                           <mrow>
                              <mi>C</mi>
                           </mrow>
                           <mrow>
                              <mi mathvariant=italic>ms</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </msqrt>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(11)
</table>
By measuring the new resonance frequency <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>sm</mi>
         </mrow>
      </msub>
   </math>, we can now estimate the moving mass
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>M</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ms</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                    <msub>
                     <mrow>
                        <mi>M</mi>
                     </mrow>
                     <mrow>
                        <mi>a</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                   <mrow>
                  <mfrac>
                     <mrow>
                         <msubsup>
                           <mrow>
                              <mi>f</mi>
                           </mrow>
                           <mrow>
                              <mi>s</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msubsup>
                     </mrow>
                     <mrow>
                        <msubsup>
                           <mrow>
                              <mi>f</mi>
                           </mrow>
                           <mrow>
                              <mi mathvariant=italic>sm</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msubsup>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-bin>−</mo>
            </mrow>
                   <mn>1</mn>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(12)
</table>
<p>and subsequently the rest of parameters
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>C</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ms</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <msup>
                     <mrow>
                        <mi>π</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <msubsup>
                    <mrow>
                        <mi>f</mi>
                    </mrow>
                    <mrow>
                        <mi>s</mi>
                    </mrow>
                    <mrow>
                        <mn>2</mn>
                    </mrow>
                    </msubsup>
                  <msub>
                     <mrow>
                        <mi>M</mi>
                     </mrow>
                     <mrow>
                        <mi>ms</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(13)
</table>
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>as</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <msup>
               <mrow>
                  <mi>c</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <msubsup>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msubsup>
            <msub>
               <mrow>
                  <mi>C</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ms</mi>
               </mrow>
            </msub>
         </math>
      <td class=eq-no>(14)
</table>
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi mathvariant=italic>Bℓ</mi>
            <mo class=MathClass-rel>=</mo>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mn>2</mn>
                        <mi>π</mi>
                        <msub>
                           <mrow>
                              <mi>f</mi>
                           </mrow>
                           <mrow>
                              <mi>s</mi>
                           </mrow>
                        </msub>
                        <msub>
                           <mrow>
                              <mi>R</mi>
                           </mrow>
                           <mrow>
                              <mi>e</mi>
                           </mrow>
                        </msub>
                         <msub>
                           <mrow>
                              <mi>M</mi>
                           </mrow>
                           <mrow>
                              <mi mathvariant=italic>ms</mi>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>Q</mi>
                           </mrow>
                           <mrow>
                              <mi mathvariant=italic>es</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>
         </math>
      <td class=eq-no>(15)
</table>
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
                    <msub>
                           <mrow>
                              <mi>R</mi>
                           </mrow>
                           <mrow>
                              <mi mathvariant=italic>ms</mi>
                           </mrow>
                        </msub>
            <mo class=MathClass-rel>=</mo>
                <mfrac>
                     <mrow>
                         <mn>1</mn>
                      </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>Q</mi>
                           </mrow>
                           <mrow>
                              <mi mathvariant=italic>ms</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                         <msub>
                           <mrow>
                              <mi>M</mi>
                           </mrow>
                           <mrow>
                              <mi mathvariant=italic>ms</mi>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>C</mi>
                           </mrow>
                           <mrow>
                              <mi mathvariant=italic>ms</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>
         </math>
      <td class=eq-no>(16)
</table>
and
 <table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>η</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>0</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
             <mfrac>           
                <mrow>
                   <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                </mrow>
                <mrow>
                    <mn>2</mn>
                    <mi>πc</mi>
                </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                    <mi>B</mi>
                    <msup>
                           <mrow>
                              <mi>ℓ</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                    </msup>
                    <msubsup>
                        <mrow>
                            <mi>S</mi>
                            </mrow>
                            <mrow>
                                <mi>d</mi>
                            </mrow>
                        <mrow>
                    <mn>2</mn>
                    </mrow>
                    </msubsup>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>e</mi>
                     </mrow>
                  </msub>
                   <msub>
                     <mrow>
                        <mi>M</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ms</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
          </math>
      <td class=eq-no>(17)
</table>
Finally, the voice coil inductance <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <msub>
        <mrow>
            <mi>L</mi>
        </mrow>
        <mrow>
            <mi mathvariant=italic>e</mi>
        </mrow>
    </msub>
     </math> can be also estimated at any frequency by measuring the reactance <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
        <mi>X</mi>
     </math> and subtracting the contribution of <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <msub>
        <mrow>
            <mi>C</mi>
        </mrow>
        <mrow>
            <mi mathvariant=italic>mes</mi>
        </mrow>
    </msub>
     </math>
   <table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>e</mi>
               </mrow>
            </msub>
             <mo>(</mo>
             <mi>f</mi>
             <mo>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>2π</mn>
                   <mi>f</mi>
               </mrow>
            </mfrac>
             <mo>(</mo>
             <mrow>
                <mi>X</mi>
                 <mo>+</mo>
                 <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>2π</mn>
                   <mi>f</mi>
                   <msub>
        <mrow>
            <mi>C</mi>
        </mrow>
        <mrow>
            <mi mathvariant=italic>mes</mi>
        </mrow>
    </msub>
               </mrow>
            </mfrac>
             </mrow>
             <mo>)</mo>
         </math>
      <td class=eq-no>(18)
</table>  

   <pr> It should be noted that, as <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <msub>
        <mrow>
            <mi>L</mi>
        </mrow>
        <mrow>
            <mi mathvariant=italic>e</mi>
        </mrow>
    </msub>
     </math> decreases with frequency, it should be measured at different frequencies to get a more precise behaviour of the inductance and, if appropriate, estimate an advanced inductance model such as the <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <msub>
        <mrow>
            <mi>L</mi>
        </mrow>
        <mrow>
            <mn>2</mn>
        </mrow>
    </msub>
     <mo>||</mo>
         <msub>
        <mrow>
            <mi>R</mi>
        </mrow>
        <mrow>
            <mn>2</mn>
        </mrow>
    </msub>
     </math> model.
<h4 class=sectionHead>Alternative methods for T&S parameter measurement</h4>
<p>Alternatively to the added mass method, the closed box method could be employed instead. This consists of enclosing the transducer on a closed box with known volume <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <msub>
        <mrow>
            <mi>V</mi>
        </mrow>
        <mrow>
            <mi mathvariant=italic>b</mi>
        </mrow>
    </msub>
     </math>. With this procedure, the stiffness of the acoustical suspension is added to the mechanical stiffness of the driver, hence resulting in an increased stiffness (this is, less compliance) that increases the resonance frequency. By measuring the new resonance frequency <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <msub>
        <mrow>
            <mi>f</mi>
        </mrow>
        <mrow>
            <mi mathvariant=italic>sb</mi>
        </mrow>
    </msub>
     </math>, it is possible to find a relationship between <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <msub>
        <mrow>
            <mi>f</mi>
        </mrow>
        <mrow>
            <mi mathvariant=italic>s</mi>
        </mrow>
    </msub>
     </math>, <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <msub>
        <mrow>
            <mi>f</mi>
        </mrow>
        <mrow>
            <mi mathvariant=italic>sb</mi>
        </mrow></msub></math>, <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow>
            <mi>V</mi>
        </mrow>
        <mrow>
            <mi mathvariant=italic>b</mi>
        </mrow>
    </msub>
     </math> and <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <msub>
        <mrow>
            <mi>V</mi>
        </mrow>
        <mrow>
            <mi mathvariant=italic>as</mi>
        </mrow>
    </msub>
     </math> that allows the estimation of <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <msub>
        <mrow>
            <mi>V</mi>
        </mrow>
        <mrow>
            <mi mathvariant=italic>as</mi>
        </mrow>
    </msub>
     </math> and, subsequently, the rest of parameters. Although the closed box method is also feasible and practical, this is less reliable than the added mass method, due to the influence of atmospheric pressure, temperature and humidity in <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <msub>
        <mrow>
            <mi>V</mi>
        </mrow>
        <mrow>
            <mi mathvariant=italic>as</mi>
        </mrow>
    </msub>
     </math>, the first parameter to be estimated. Such inaccuracies in the estimation of <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <msub>
        <mrow>
            <mi>V</mi>
        </mrow>
        <mrow>
            <mi mathvariant=italic>as</mi>
        </mrow>
    </msub>
     </math> are further transferred to the next parameters to be estimated. Therefore, the added mass method is generally preferred.

<p>On the other hand, a more precise method using a laser sensor that measures the movement of the membrane could be also employed. By means of a triangulation laser, the displacement of the cone can be measured. Alternatively, a Doppler laser could be used, which is able to measure the cone velocity. Either using a triangulation or a Doppler laser, it is possible to find a relationship between the input voltage and the mechanical response, and thus estimate <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi mathvariant=italic>Bℓ</mi>
      </math>. Subsequently, the rest of parameters can be estimated --- in a different order than the previous methods. In general, this method is more precise, although it requires a more complex and expensive instrumentation. 
<h3 class=sectionHead>Conclusion</h3>
<p>In this Lesson, T&S parameters that allow the modelling of a moving-coil loudspeaker at low-frequencies and small-signal input voltage were presented. A simple yet reliable measurement method with affordable equipment ---the added mass method--- was also described. Future Lessons will address the limitations of the low frequency/small displacement model.
<h3 class=likesectionHead>Glossary</h3>
<p>
<dl>
  <dt>1-DOF
  <dd>1 Degree Of Freedom
  <dt>DC
  <dd>Direct Current
  <dt>T&S
  <dd>Thiele & Small
</dl>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt id=X0-d1998testing class=thebibliography>[1]
   <dd id=bib-1 class=thebibliography>
      <p>Joseph A D’Appolito. <span class=cmti-10>Testing loudspeakers</span>. Audio Amateur Press, 1998. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 9781882580170.
   <dt id=X0-small1972direct class=thebibliography>[2]
   <dd id=bib-2 class=thebibliography>
      <p>Richard H Small. “Direct radiator loudspeaker system analysis”. In: <span class=cmti-10>Journal of the Audio Engineering Society </span>20.5 (1972), pp. 383–395.
</dl>`;

export default theory;
